import { isDefined, isEmpty } from "@clipboard-health/util-ts";
import { Alert, Autocomplete, CircularProgress, Stack, TextField } from "@mui/material";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { useDebounce } from "@src/appV2/lib/utils";
import { formatCbhApiError, isCbhApiAxiosError } from "@src/appV2/lib/utils/errorUtils";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { useCloneNfcHashes } from "../api/useCloneNfcHashes";

export interface CloneNfcTagsOriginWorkplace {
  id: string;
  name: string;
  address?: string;
}

interface DestinationWorkplace {
  id: string;
  name: string;
}

export interface CloneNfcTagsDialogProps {
  modalState: UseModalState;
  origin: CloneNfcTagsOriginWorkplace;
  /**
   * Workplace `userId` to pre-select as the destination (e.g. the source's
   * `changeOfOwnership.replacementFacilityId`). Its name is resolved for display;
   * the admin can still change the selection.
   */
  defaultDestinationWorkplaceId?: string;
}

type DialogStep = "form" | "success";

/** Extracts a user-facing message from a clone failure, falling back to a generic message. */
function getErrorMessage(error: unknown): string {
  if (isCbhApiAxiosError(error)) {
    const firstError = error.response.data.errors[0];
    if (isDefined(firstError)) {
      const formatted = formatCbhApiError(firstError);
      if (formatted.length > 0) {
        return formatted;
      }
    }
  }
  return "Failed to clone NFC tags. Please try again or contact support.";
}

/** Returns the destination autocomplete's empty-state text for the current search/loading state. */
function getNoOptionsText(searchInput: string, isLoading: boolean): string {
  if (isEmpty(searchInput)) {
    return "Start typing to search for workplaces";
  }
  return isLoading ? "Searching workplaces..." : "No workplaces found";
}

/**
 * Modal for cloning the source workplace's active NFC tags into a destination
 * workplace. The admin searches for and selects the destination (optionally
 * pre-filled from the change-of-ownership replacement), confirms, and sees a
 * success summary. Errors are surfaced inline.
 */
export function CloneNfcTagsDialog(props: CloneNfcTagsDialogProps) {
  const { modalState, origin, defaultDestinationWorkplaceId } = props;

  const [step, setStep] = useState<DialogStep>("form");
  const [destination, setDestination] = useState<DestinationWorkplace | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [clonedCount, setClonedCount] = useState(0);
  const [didPrefillDestination, setDidPrefillDestination] = useState(false);

  const debouncedSearch = useDebounce({ debounceTimeInMs: 300, value: searchInput });

  const { mutateAsync: cloneNfcHashes, isLoading } = useCloneNfcHashes();

  // Resolve the change-of-ownership replacement workplace so we can show its name
  // in the selector. It is a workplace `userId`, the same id the clone endpoint uses.
  const shouldPrefillDestination =
    isDefined(defaultDestinationWorkplaceId) &&
    defaultDestinationWorkplaceId.length > 0 &&
    defaultDestinationWorkplaceId !== origin.id;

  const { data: defaultDestinationProfile } = useGetFacilityProfile(
    defaultDestinationWorkplaceId ?? "",
    // Space-separated: the facility-profile API splits projections on spaces, so a comma-joined
    // value is treated as one (forbidden) field and 400s.
    "name userId",
    { enabled: shouldPrefillDestination }
  );

  // Pre-populate the selector once from the change-of-ownership field. The admin
  // can still clear or change it afterwards, so we never override a later edit.
  useEffect(() => {
    if (!didPrefillDestination && !isDefined(destination) && isDefined(defaultDestinationProfile)) {
      setDestination({
        id: defaultDestinationProfile.userId,
        name: defaultDestinationProfile.name,
      });
      setDidPrefillDestination(true);
    }
  }, [didPrefillDestination, destination, defaultDestinationProfile]);

  const { data: facilitiesData, isFetching: facilitiesLoading } = useSearchFacilities(
    { searchInput: debouncedSearch },
    { enabled: debouncedSearch.length > 0 }
  );

  const destinationOptions: DestinationWorkplace[] =
    facilitiesData?.pages
      .flatMap((page) => page.list)
      .filter((facility: Facility) => facility.userId !== origin.id)
      .map((facility: Facility) => ({ id: facility.userId, name: facility.name })) ?? [];

  const resetAndClose = () => {
    setStep("form");
    setDestination(null);
    setSearchInput("");
    setErrorMessage(undefined);
    setClonedCount(0);
    setDidPrefillDestination(false);
    modalState.closeModal();
  };

  const handleClone = async () => {
    if (!isDefined(destination)) {
      return;
    }
    setErrorMessage(undefined);
    try {
      const response = await cloneNfcHashes({
        targetWorkplaceId: destination.id,
        sourceWorkplaceId: origin.id,
      });
      setClonedCount(response.data.length);
      setStep("success");
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  let title: string;
  let body: React.ReactNode;
  let footer: React.ReactNode;

  if (step === "success") {
    title = "NFC tags cloned";
    body = (
      <Text variant="body2">
        Cloned {clonedCount} NFC tag(s) from {origin.name} into{" "}
        {destination?.name ?? "the destination workplace"}. The cloned tags are now in Set up status
        on the destination workplace.
      </Text>
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: "Close",
          onClick: resetAndClose,
        }}
      />
    );
  } else {
    title = "Clone NFC tags";
    body = (
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Text semibold variant="body2">
            Source workplace
          </Text>
          <Text variant="body2">{origin.name}</Text>
          <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
            {origin.address ?? "Address unavailable"}
          </Text>
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            Destination workplace
          </Text>
          <Autocomplete
            autoComplete
            disabled={isLoading}
            filterOptions={(options) => options}
            getOptionKey={(option) => (typeof option === "string" ? option : option.id)}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string" || typeof value === "string") {
                return false;
              }
              return option.id === value.id;
            }}
            loading={facilitiesLoading}
            loadingText="Searching workplaces..."
            noOptionsText={getNoOptionsText(searchInput, facilitiesLoading)}
            options={destinationOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start typing workplace name..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {facilitiesLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(renderProps, option) => {
              if (!option) {
                return null;
              }
              return (
                <li {...renderProps} key={option.id}>
                  <Stack direction="column">
                    <Text variant="body2">{option.name}</Text>
                    <Text
                      sx={(theme) => ({ color: theme.palette.text.secondary })}
                      variant="caption"
                    >
                      ID: {option.id}
                    </Text>
                  </Stack>
                </li>
              );
            }}
            value={destination}
            onChange={(_, newValue) => {
              if (typeof newValue !== "string") {
                setDestination(newValue);
              }
            }}
            onInputChange={(_, newInputValue) => {
              setSearchInput(newInputValue);
            }}
          />
        </Stack>

        <Alert severity="info">
          This clones all active NFC tags from {origin.name} into the destination workplace so the
          same physical posters work for both. Cloned tags are added in Set up status. A workplace
          can hold at most 10 tags, and tags that already exist on the destination are skipped.
        </Alert>

        {isDefined(errorMessage) && <Alert severity="error">{errorMessage}</Alert>}
      </Stack>
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: isLoading ? "Cloning..." : "Clone tags",
          isLoading,
          disabled: !isDefined(destination),
          onClick: () => {
            void handleClone();
          },
        }}
        secondaryAction={{
          label: "Cancel",
          disabled: isLoading,
          onClick: resetAndClose,
        }}
      />
    );
  }

  return (
    <BerlinThemeProvider>
      <ResponsiveDialog
        showCloseButton
        footerContent={footer}
        modalState={modalState}
        title={title}
        width={520}
        onClose={resetAndClose}
      >
        {body}
      </ResponsiveDialog>
    </BerlinThemeProvider>
  );
}
